import { ui } from '@procuros/datachecks';
import { ColumnDef } from '@tanstack/react-table';

import { logError } from 'services/logging/logging';
import { ColumnTypesInternal, WebEDIRowData } from 'support/types/webediTable';

import { extractChildKey } from './pathsUtils';

type ValidationResult = { valid: true; type: ui.FieldTypes } | { valid: false };

type NestedColumnData = {
  key: string;
  embeddedTableKey: string;
  format: ui.FieldTypes;
};

/**
 * Extracts flattened headers from columns for Excel template validation
 * Regular columns use their ID directly, embedded table columns use "parent.*.child" format
 *
 * @param columns - Column definitions from TanStack Table
 * @returns Array of flattened header strings
 */
export const extractFlattenedHeaders = (columns: Array<ColumnDef<WebEDIRowData, unknown>>): Array<string> => {
  const headers: Array<string> = [];

  columns.forEach((column) => {
    if (columnContainsNestedColumns(column) && column.id) {
      const parentId = column.id;
      column.columns.forEach((subCol) => {
        const nestedData = processNestedColumn(parentId, subCol);
        if (nestedData) {
          headers.push(nestedData.key);
        }
      });
    } else {
      const validation = validateColumn(column);
      if (validation.valid && column.id) {
        headers.push(column.id);
      }
    }
  });

  return headers;
};

/**
 * Extracts format mappings from columns for type conversion during import
 * Maps each column key to its ui.FieldTypes format
 *
 * @param columns - Column definitions from TanStack Table
 * @returns Record mapping column keys to field types
 */
export const extractExpectedDataFormats = (
  columns: Array<ColumnDef<WebEDIRowData, unknown>>,
): Record<string, ui.FieldTypes> => {
  const formats: Record<string, ui.FieldTypes> = {};

  columns.forEach((column) => {
    if (columnContainsNestedColumns(column) && column.id) {
      const parentId = column.id;
      column.columns.forEach((subCol) => {
        const nestedData = processNestedColumn(parentId, subCol);
        if (nestedData) {
          formats[nestedData.key] = nestedData.format;
        }
      });
    } else {
      const validation = validateColumn(column);
      if (validation.valid && column.id) {
        formats[column.id] = validation.type;
      }
    }
  });

  return formats;
};

/**
 * Extracts main column metadata (non-embedded table columns)
 * Used for processing regular columns during data transformation
 *
 * @param columns - Column definitions from TanStack Table
 * @returns Array of main column metadata with key and format
 */
export const extractMainColumns = (
  columns: Array<ColumnDef<WebEDIRowData, unknown>>,
): Array<{ key: string; format: ui.FieldTypes }> => {
  const mainColumns: Array<{ key: string; format: ui.FieldTypes }> = [];

  columns.forEach((column) => {
    if (!columnContainsNestedColumns(column)) {
      const validation = validateColumn(column);
      if (validation.valid && column.id) {
        mainColumns.push({
          key: column.id,
          format: validation.type,
        });
      }
    }
  });

  return mainColumns;
};

/**
 * Extracts embedded table group metadata for multi-column fields
 * Groups sub-columns under their parent for row building during transformation
 *
 * @param columns - Column definitions from TanStack Table
 * @returns Array of embedded table groups with parent key and sub-columns
 */
export const extractEmbeddedTableGroups = (
  columns: Array<ColumnDef<WebEDIRowData, unknown>>,
): Array<{
  parentKey: string;
  subColumns: Array<{
    key: string;
    embeddedTableKey: string;
    format: ui.FieldTypes;
  }>;
}> => {
  const groups: Array<{
    parentKey: string;
    subColumns: Array<{
      key: string;
      embeddedTableKey: string;
      format: ui.FieldTypes;
    }>;
  }> = [];

  columns.forEach((column) => {
    if (columnContainsNestedColumns(column) && column.id) {
      const parentId = column.id;
      const subColumns: Array<{
        key: string;
        embeddedTableKey: string;
        format: ui.FieldTypes;
      }> = [];

      column.columns.forEach((subCol) => {
        const nestedData = processNestedColumn(parentId, subCol);
        if (nestedData) {
          subColumns.push({
            key: nestedData.key,
            embeddedTableKey: nestedData.embeddedTableKey,
            format: nestedData.format,
          });
        }
      });

      if (subColumns.length > 0) {
        groups.push({
          parentKey: parentId,
          subColumns,
        });
      }
    }
  });

  return groups;
};

/**
 * Type guard to validate field types at runtime
 * Ensures meta.type values are valid ui.FieldTypes before use
 */
const isValidFieldType = (type: unknown): type is ui.FieldTypes => {
  return Object.values(ui.FieldTypes).includes(type as ui.FieldTypes);
};

/**
 * Checks if a column should be excluded from Excel templates
 * Internal columns (select, delete, added, index) should not appear in templates
 */
const shouldExcludeColumn = (columnId: string): boolean => {
  return Object.values(ColumnTypesInternal).some((internalType) => {
    return (
      columnId === internalType ||
      columnId.startsWith(`${internalType}.`) ||
      columnId.startsWith(`${internalType}_`) ||
      columnId.endsWith(`.${internalType}`)
    );
  });
};

const columnContainsNestedColumns = (
  column: ColumnDef<WebEDIRowData, unknown>,
): column is ColumnDef<WebEDIRowData, unknown> & { columns: Array<ColumnDef<WebEDIRowData, unknown>> } => {
  return 'columns' in column && Array.isArray(column.columns) && column.columns.length > 0;
};

const validateColumn = (column: ColumnDef<WebEDIRowData, unknown>): ValidationResult => {
  if (!column.id || !column.meta?.type || shouldExcludeColumn(column.id)) {
    return { valid: false };
  }

  if (!isValidFieldType(column.meta.type)) {
    logError(`Invalid field type "${column.meta.type}" for column "${column.id}". Skipping.`);
    return { valid: false };
  }

  return { valid: true, type: column.meta.type };
};

const processNestedColumn = (parentId: string, subCol: ColumnDef<WebEDIRowData, unknown>): NestedColumnData | null => {
  const validation = validateColumn(subCol);
  if (!validation.valid || !subCol.id) {
    return null;
  }

  // subCol.id already contains the full key (e.g., "packaging_units.*.item.length")
  // No need to reconstruct it - just use it directly
  return {
    key: subCol.id,
    embeddedTableKey: extractChildKey(subCol.id),
    format: validation.type,
  };
};
